/**
 * useCategoryHelpers Hook
 * 
 * Provides helper functions for categories, groups, and details.
 */

import { useMemo } from "react";
import { TransactionCategory, TransactionCategoryGroup, TransactionCategoryDetail, Transaction } from "../../../types";

export interface UseCategoryHelpersParams {
  categories: TransactionCategory[];
  groups: TransactionCategoryGroup[];
  transactions: Transaction[];
}

export interface UseCategoryHelpersReturn {
  // Grouping
  groupedCategories: Record<string, TransactionCategory[]>;
  
  // Usage checking
  isCategoryUsed: (categoryName: string) => boolean;
  isDetailUsed: (categoryName: string, detailName: string) => boolean;
  isGroupUsed: (groupId: string) => boolean;
  isGroupDetailUsed: (groupName: string, detailName: string) => boolean;
  
  // Counting
  countCategoriesInGroup: (groupId: string) => number;
  countTransactionsInGroup: (groupId: string) => number;
  
  // Change detection
  hasDetailsChanged: (
    editingDetails: TransactionCategoryDetail[],
    originalDetails: TransactionCategoryDetail[]
  ) => boolean;
  hasGroupDetailsChanged: (
    editingGroupDetails: TransactionCategoryDetail[],
    originalGroupDetails: TransactionCategoryDetail[]
  ) => boolean;
  hasPartialFormData: (
    newDetailName: string,
    newDetailType: "list" | "measurable" | "",
    newDetailUnit: string,
    newListItems: string[],
    editingDetailIndex: number | null
  ) => boolean;
  hasEditedDetailChanged: (
    editingDetailIndex: number | null,
    editingOriginalDetail: TransactionCategoryDetail | null,
    newDetailName: string,
    newDetailType: "list" | "measurable" | "",
    newDetailUnit: string,
    newListItems: string[]
  ) => boolean;
  hasDetailSelectionChanged: (
    enabledDetails: string[],
    originalEnabledDetails: string[],
    defaultValues: Record<string, string | number>,
    originalDefaultValues: Record<string, string | number>
  ) => boolean;
}

/**
 * Hook for category and group helper functions
 */
export function useCategoryHelpers({
  categories,
  groups,
  transactions,
}: UseCategoryHelpersParams): UseCategoryHelpersReturn {
  // Group categories by group (ordered by groups order, then by category order)
  const groupedCategories: Record<string, TransactionCategory[]> = useMemo(() => {
    const grouped: Record<string, TransactionCategory[]> = {};
    groups.forEach((group) => {
      const groupCategories = categories.filter((cat) => cat.groupId === group.id);
      // Sort by order field (default to 0 if not present)
      groupCategories.sort((a, b) => {
        const orderA = (a as any).order ?? 0;
        const orderB = (b as any).order ?? 0;
        return orderA - orderB;
      });
      grouped[group.id] = groupCategories;
    });
    return grouped;
  }, [categories, groups]);

  // Check if a category is used in transactions
  const isCategoryUsed = (categoryName: string): boolean => {
    if (!categoryName) return false;
    return transactions.some((transaction) => transaction.category === categoryName);
  };

  // Check if a detail is used in transactions for a specific category
  const isDetailUsed = (categoryName: string, detailName: string): boolean => {
    if (!categoryName || !detailName) return false;
    
    const isUsed = transactions.some((transaction) => {
      // Check if transaction belongs to this category
      if (!transaction.category || transaction.category !== categoryName) {
        return false;
      }
      
      // Check if transaction has details
      if (!transaction.details) {
        return false;
      }
      
      // Ensure details is an object
      if (typeof transaction.details !== 'object' || Array.isArray(transaction.details)) {
        return false;
      }
      
      // Check if the detail name exists in the transaction details
      // Use 'in' operator for direct property check
      return detailName in transaction.details;
    });
    
    return isUsed;
  };

  // Check if a group is used (has categories assigned to it)
  const isGroupUsed = (groupId: string): boolean => {
    return categories.some((category) => category.groupId === groupId);
  };

  // Count categories using this group
  const countCategoriesInGroup = (groupId: string): number => {
    return categories.filter((category) => category.groupId === groupId).length;
  };

  // Count transactions using categories in this group
  const countTransactionsInGroup = (groupId: string): number => {
    const categoryIds = categories
      .filter((category) => category.groupId === groupId)
      .map((category) => category.id);
    return transactions.filter((transaction) => 
      transaction.category && categoryIds.includes(transaction.category)
    ).length;
  };

  // Check if a group detail is used in transactions
  const isGroupDetailUsed = (groupName: string, detailName: string): boolean => {
    if (!groupName || !detailName) return false;
    
    // Find categories in this group
    const groupCategories = categories.filter((cat) => {
      const group = cat.groupId ? groups.find((g) => g.id === cat.groupId) : null;
      return group?.name === groupName;
    });
    
    // Check if any transaction uses this detail for any category in this group
    return transactions.some((transaction) => {
      // Check if transaction belongs to a category in this group
      const transactionCategory = groupCategories.find((cat) => cat.name === transaction.category);
      if (!transactionCategory) {
        return false;
      }
      
      // Check if transaction has details
      if (!transaction.details) {
        return false;
      }
      
      // Ensure details is an object
      if (typeof transaction.details !== 'object' || Array.isArray(transaction.details)) {
        return false;
      }
      
      // Check if the detail name exists in the transaction details
      return detailName in transaction.details;
    });
  };

  // Check if details have changed (legacy - for category-level details)
  const hasDetailsChanged = (
    editingDetails: TransactionCategoryDetail[],
    originalDetails: TransactionCategoryDetail[]
  ): boolean => {
    // Compare arrays - check if length is different
    if (editingDetails.length !== originalDetails.length) {
      return true;
    }
    
    // If both are empty, no changes
    if (editingDetails.length === 0 && originalDetails.length === 0) {
      return false;
    }
    
    // Check if order has changed (compare arrays in original order)
    const orderChanged = editingDetails.some((detail, index) => {
      const original = originalDetails[index];
      if (!original) return true;
      return detail.name !== original.name || detail.type !== original.type;
    });
    
    if (orderChanged) {
      return true;
    }
    
    // Deep comparison of details
    for (let i = 0; i < originalDetails.length; i++) {
      const original = originalDetails[i];
      const editing = editingDetails[i];
      
      if (!original || !editing) return true;
      
      if (original.name !== editing.name || original.type !== editing.type) {
        return true;
      }
      
      // Check listItems for list type details
      if (original.type === "list" || editing.type === "list") {
        const originalItems = original.listItems || [];
        const editingItems = editing.listItems || [];
        if (originalItems.length !== editingItems.length) {
          return true;
        }
        const originalItemsSorted = [...originalItems].sort();
        const editingItemsSorted = [...editingItems].sort();
        if (originalItemsSorted.join(",") !== editingItemsSorted.join(",")) {
          return true;
        }
      }
      
      // Check unit for measurable type details
      if (original.type === "measurable" || editing.type === "measurable") {
        if ((original.unit || "") !== (editing.unit || "")) {
          return true;
        }
      }
    }
    
    return false;
  };

  // Check if group details have changed
  const hasGroupDetailsChanged = (
    editingGroupDetails: TransactionCategoryDetail[],
    originalGroupDetails: TransactionCategoryDetail[]
  ): boolean => {
    // Compare arrays - check if length is different
    if (editingGroupDetails.length !== originalGroupDetails.length) {
      return true;
    }
    
    // If both are empty, no changes
    if (editingGroupDetails.length === 0 && originalGroupDetails.length === 0) {
      return false;
    }
    
    // Check if order has changed (compare arrays in original order)
    const orderChanged = editingGroupDetails.some((detail, index) => {
      const original = originalGroupDetails[index];
      if (!original) return true;
      return detail.name !== original.name || detail.type !== original.type;
    });
    
    if (orderChanged) {
      return true;
    }
    
    // Deep comparison of details
    for (let i = 0; i < originalGroupDetails.length; i++) {
      const original = originalGroupDetails[i];
      const editing = editingGroupDetails[i];
      
      if (!original || !editing) return true;
      
      if (original.name !== editing.name || original.type !== editing.type) {
        return true;
      }
      
      // Check listItems for list type details
      if (original.type === "list" || editing.type === "list") {
        const originalItems = original.listItems || [];
        const editingItems = editing.listItems || [];
        if (originalItems.length !== editingItems.length) {
          return true;
        }
        const originalItemsSorted = [...originalItems].sort();
        const editingItemsSorted = [...editingItems].sort();
        if (originalItemsSorted.join(",") !== editingItemsSorted.join(",")) {
          return true;
        }
      }
      
      // Check unit for measurable type details
      if (original.type === "measurable" || editing.type === "measurable") {
        if ((original.unit || "") !== (editing.unit || "")) {
          return true;
        }
      }
    }
    
    return false;
  };

  // Check if form has partial data
  const hasPartialFormData = (
    newDetailName: string,
    newDetailType: "list" | "measurable" | "",
    newDetailUnit: string,
    newListItems: string[],
    editingDetailIndex: number | null
  ): boolean => {
    return (newDetailName.trim().length > 0 || newDetailType.length > 0 || newDetailUnit.trim().length > 0 || newListItems.length > 0) && editingDetailIndex === null;
  };

  // Check if the detail being edited has changed from the original
  const hasEditedDetailChanged = (
    editingDetailIndex: number | null,
    editingOriginalDetail: TransactionCategoryDetail | null,
    newDetailName: string,
    newDetailType: "list" | "measurable" | "",
    newDetailUnit: string,
    newListItems: string[]
  ): boolean => {
    if (editingDetailIndex === null || !editingOriginalDetail) {
      return false;
    }

    const original = editingOriginalDetail;
    const editedName = newDetailName.trim();
    const editedType = newDetailType;
    const editedUnit = newDetailUnit.trim();
    const editedListItems = [...newListItems].sort();

    // Check name
    if (original.name !== editedName) {
      return true;
    }

    // Check type
    if (original.type !== editedType) {
      return true;
    }

    // Check unit for measurable type
    if (original.type === "measurable" || editedType === "measurable") {
      const originalUnit = (original.unit || "").trim();
      if (originalUnit !== editedUnit) {
        return true;
      }
    }

    // Check list items for list type
    if (original.type === "list" || editedType === "list") {
      const originalItems = [...(original.listItems || [])].sort();
      if (originalItems.length !== editedListItems.length) {
        return true;
      }
      if (originalItems.join(",") !== editedListItems.join(",")) {
        return true;
      }
    }

    return false;
  };

  // Check if detail selection has changed
  const hasDetailSelectionChanged = (
    enabledDetails: string[],
    originalEnabledDetails: string[],
    defaultValues: Record<string, string | number>,
    originalDefaultValues: Record<string, string | number>
  ): boolean => {
    if (enabledDetails.length !== originalEnabledDetails.length) {
      return true;
    }
    
    const enabledSet = new Set(enabledDetails);
    const originalSet = new Set(originalEnabledDetails);
    if (enabledSet.size !== originalSet.size) {
      return true;
    }
    
    for (const detail of enabledDetails) {
      if (!originalSet.has(detail)) {
        return true;
      }
    }
    
    // Check default values
    const defaultKeys = Object.keys(defaultValues);
    const originalDefaultKeys = Object.keys(originalDefaultValues);
    
    if (defaultKeys.length !== originalDefaultKeys.length) {
      return true;
    }
    
    for (const key of defaultKeys) {
      if (defaultValues[key] !== originalDefaultValues[key]) {
        return true;
      }
    }
    
    return false;
  };

  return {
    groupedCategories,
    isCategoryUsed,
    isDetailUsed,
    isGroupUsed,
    isGroupDetailUsed,
    countCategoriesInGroup,
    countTransactionsInGroup,
    hasDetailsChanged,
    hasGroupDetailsChanged,
    hasPartialFormData,
    hasEditedDetailChanged,
    hasDetailSelectionChanged,
  };
}
